import { useState, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface DisciplineTranslationUpdate {
  name_en: string | null;
  abbreviation_en: string | null;
  group_code_en: string | null;
}

// The English counterparts of the Estonian columns in DesignDisciplinesTable,
// in table order. English-language pills show these codes and fall back to
// the Estonian ones while they are empty.
const TRANSLATION_FIELDS = [
  { field: "name_en", label: "Name (EN)", placeholder: "English name", width: "35%" },
  { field: "abbreviation_en", label: "Abbreviation (EN)", placeholder: "e.g. LIFT", width: "13%" },
  { field: "group_code_en", label: "Group code (EN)", placeholder: "e.g. HVAC", width: "13%" },
] as const;

const DisciplinesTranslationsTable = observer(() => {
  const { designDisciplinesStore } = useStores();
  const [updates, setUpdates] = useState<Record<number, DisciplineTranslationUpdate>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    designDisciplinesStore.loadDisciplines();
  }, [designDisciplinesStore]);

  useEffect(() => {
    const initial: Record<number, DisciplineTranslationUpdate> = {};
    designDisciplinesStore.disciplinesList.forEach((discipline) => {
      initial[discipline.id] = {
        name_en: discipline.name_en,
        abbreviation_en: discipline.abbreviation_en,
        group_code_en: discipline.group_code_en,
      };
    });
    setUpdates(initial);
  }, [designDisciplinesStore.disciplinesList]);

  const handleSave = async (disciplineId: number) => {
    const update = updates[disciplineId];
    if (!update) return;

    setSavingId(disciplineId);
    try {
      await designDisciplinesStore.updateDiscipline(disciplineId, update);
      toast.success("Discipline translation updated successfully");
    } catch (error) {
      toast.error("Failed to update discipline translation");
    } finally {
      setSavingId(null);
    }
  };

  const handleFieldChange = (
    disciplineId: number,
    field: keyof DisciplineTranslationUpdate,
    value: string | null
  ) => {
    setUpdates((prev) => ({
      ...prev,
      [disciplineId]: { ...prev[disciplineId], [field]: value },
    }));
  };

  const filteredDisciplines = useMemo(() => {
    if (!searchQuery.trim()) {
      return designDisciplinesStore.disciplinesList;
    }

    const query = searchQuery.toLowerCase();
    return designDisciplinesStore.disciplinesList.filter((discipline) => {
      return (
        discipline.name.toLowerCase().includes(query) ||
        TRANSLATION_FIELDS.some(({ field }) => discipline[field]?.toLowerCase().includes(query))
      );
    });
  }, [designDisciplinesStore.disciplinesList, searchQuery]);

  if (designDisciplinesStore.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (designDisciplinesStore.error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-destructive">
        Error loading design disciplines: {designDisciplinesStore.error}
      </div>
    );
  }

  if (filteredDisciplines.length === 0) {
    return (
      <>
        <div className="mb-4">
          <Input
            placeholder="Search design disciplines..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1"
          />
        </div>
        <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground">
          {searchQuery.trim()
            ? "No design disciplines match your search."
            : "No design disciplines found."}
        </div>
      </>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Input
          placeholder="Search design disciplines..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
      </div>

      <div className="rounded-md border">
        <Table className="w-full" style={{ tableLayout: "fixed" }}>
          <TableHeader>
            <TableRow>
              <TableHead style={{ width: "25%" }}>Discipline Name (ET)</TableHead>
              {TRANSLATION_FIELDS.map(({ field, label, width }) => (
                <TableHead key={field} style={{ width }}>
                  {label}
                </TableHead>
              ))}
              <TableHead style={{ width: "14%" }} className="text-right">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDisciplines.map((discipline) => {
              const update = updates[discipline.id];
              if (!update) return null;

              const hasChanges = TRANSLATION_FIELDS.some(
                ({ field }) => update[field] !== discipline[field]
              );

              return (
                <TableRow key={discipline.id}>
                  <TableCell style={{ width: "25%" }}>
                    <span className="text-sm font-medium">{discipline.name}</span>
                  </TableCell>
                  {TRANSLATION_FIELDS.map(({ field, label, placeholder, width }) => (
                    <TableCell key={field} style={{ width }}>
                      <Input
                        value={update[field] || ""}
                        onChange={(e) =>
                          handleFieldChange(discipline.id, field, e.target.value || null)
                        }
                        placeholder={placeholder}
                        aria-label={`${label} — ${discipline.name}`}
                        className="h-8 w-full"
                      />
                    </TableCell>
                  ))}
                  <TableCell style={{ width: "14%" }} className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSave(discipline.id)}
                      disabled={!hasChanges || savingId === discipline.id}
                    >
                      {savingId === discipline.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
});

export default DisciplinesTranslationsTable;
